import { Button, Card, Col, Divider, Flex, Form, FormProps, Input, Radio, RadioChangeEvent, Row, Segmented, Typography } from 'antd';
import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FileInput from 'components/fileInput/fileInput';
import SelectElement from 'components/selectElement/selectElement';
import PageTitle from 'components/title/title';
import { InfoCircleOutlined } from '@ant-design/icons';

// services

// models
import { Period, PeriodType } from '../models/financialStatement';
import VerificationModal from '../../verificationModal';
import ListButton from 'components/listButton/listButton';
import Icon from 'utils/icon';
import NotificationService from 'core/services/notification';
import { onCheckFinancialStatementExists, onSubmitFinancialStatement } from '../services/financialStatementServices';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { VerificationPayload } from '../../commonModels';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';

interface ActionFinancialStatementCreatePageProps {}

type FieldsType = {
	year: string;
	audience: string;
	period: string;
	period_type: string;
	type: string;
	files: any[];
};

const ActionFinancialStatementCreatePage: FunctionComponent<ActionFinancialStatementCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [financialStatementForm] = Form.useForm();

	const periodType = Form.useWatch('period_type', financialStatementForm) as PeriodType;
	const period = Form.useWatch('period', financialStatementForm) as Period;
	const year = Form.useWatch('year', financialStatementForm) as number;
	const type = Form.useWatch('type', financialStatementForm);
	const audience = Form.useWatch('audience', financialStatementForm);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isChecking, setIsChecking] = useState<boolean>(false);
	const [isExist, setIsExist] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	const [typeOptions, setTypeOptions] = useState<any[]>([
		{ value: 'Internal', label: t('ACTIONS.INTERNAL') },
		{ value: 'Audited', label: t('ACTIONS.AUDITED') },
	]);

	const audienceTypeOptions = [
		{
			value: 'Shareholders',
			label: t('COMMON.SHAREHOLDERS'),
		},
		{
			value: 'Public',
			label: t('ACTIONS.PUBLIC'),
		},
	];

	const periodTypeOptions = [
		{
			value: 'Annual',
			label: <p className="fw-semibold mb-0">{t('ACTIONS.ANNUAL')}</p>,
		},
		{
			value: 'HalfYear',
			label: <p className="fw-semibold mb-0">{t('ACTIONS.SEMI_ANNUAL')}</p>,
		},
		{
			value: 'Quarter',
			label: <p className="fw-semibold mb-0">{t('ACTIONS.QUARTER')}</p>,
		},
	];

	const yearsOptions = Array.from({ length: 6 }, (_, i) => {
		const year = new Date().getFullYear() - i;
		return { value: year, label: String(year) };
	});

	useEffect(() => {
		onCheckIfExist();
	}, [periodType, period, year, type]);

	const onChangePeriod = (e: RadioChangeEvent) => {
		const selectedPeriod = e.target.value as PeriodType;

		if (selectedPeriod === 'Annual') {
			setTypeOptions([
				{ value: 'Internal', label: t('ACTIONS.INTERNAL') },
				{ value: 'Audited', label: t('ACTIONS.AUDITED') },
			]);
		} else {
			setTypeOptions([{ value: 'Internal', label: t('ACTIONS.INTERNAL') }]);
			if (financialStatementForm.getFieldValue('type') === 'Audited') financialStatementForm.setFieldValue('type', null);
		}
	};

	const onCheckIfExist = async () => {
		try {
			if ((period && periodType === 'Quarter' && year && type) || (periodType !== 'Quarter' && year && type)) {
				setIsChecking(true);
				const response = await onCheckFinancialStatementExists({ period: periodType, quarter: period, year, type });
				setIsExist(response.exists);
			}

			setIsChecking(false);
		} catch (error) {
			setIsChecking(false);
		}
	};

	const onVerifyData: FormProps<FieldsType>['onFinish'] = (values) => {
		if (isExist)
			financialStatementForm.setFields([
				{
					name: 'year',
					errors: [''],
				},
				{
					name: 'period',
					errors: [''],
				},
				{
					name: 'period_type',
					errors: [''],
				},
				{
					name: 'type',
					errors: [''],
				},
			]);

		if (!isExist) setVerificationModalOpen(true);
	};

	const onSubmit = async ({ key, method }: VerificationPayload) => {
		try {
			setIsLoading(true);
			const payload = new FormData();

			payload.append('year', String(year));
			payload.append('period', periodType);
			payload.append('type', type);
			payload.append('audience', financialStatementForm.getFieldValue('audience'));
			payload.append('verified_using', method);
			payload.append(method, key);

			period && payload.append('quarter', period);

			financialStatementForm.getFieldValue('files')?.forEach((fileObj: { file: File; title: string }, index: number) => {
				payload.append(`files[${index}][file]`, fileObj.file);
				payload.append(`files[${index}][title]`, fileObj.title);
			});

			await onSubmitFinancialStatement(payload);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.FINANCIAL_STATEMENT_CREATED'));

			setIsLoading(false);
			navigate('/dashboard/actions/financial-statement');
		} catch (error: any) {
			setIsLoading(false);
			mapResponseErrors(error, financialStatementForm);

			const errorMessage = error?.response?.data?.data?.errors;
			const messages = Object.values(errorMessage || {}).flat() as string[];

			const errors = (
				<ul>
					{messages.map((msg, index) => (
						<li key={index}>{msg}</li>
					))}
				</ul>
			);

			NotificationService.error(t('COMMON.ERROR'), messages.length ? errors : t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('ACTIONS.PUBLISH_FINANCIAL_STATEMENT')} subtitle={t('ACTIONS.FILL_TO_REVIEW')} />

				<Form
					form={financialStatementForm}
					onFinish={onVerifyData}
					initialValues={{ audience: 'Shareholders', period_type: 'Annual', files: [{}] }}>
					<div>
						<h6 className="mt-4 mb-2">{t('ACTIONS.AUDIENCE')}</h6>

						<Form.Item name="audience" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]} style={{ maxWidth: 'unset' }}>
							<Segmented
								className="custom white"
								onChange={(value) => financialStatementForm.setFieldValue('audience', value)}
								options={audienceTypeOptions}
								block
								size="large"
								style={{ marginBottom: 30 }}
							/>
						</Form.Item>

						<p>{audience === 'Public' ? t('ACTIONS.PUBLIC_DESC') : t('ACTIONS.TO_SENT_TO_SHAREHOLDERS')}</p>
					</div>

					<div>
						<h6 className="mt-4 mb-2">{t('ACTIONS.PERIOD')}</h6>
						<Form.Item name="period_type" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
							<Radio.Group
								onChange={onChangePeriod}
								style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
								options={periodTypeOptions}
							/>
						</Form.Item>
					</div>

					<Row gutter={[16, 16]} className="mt-5">
						<Col span={24} md={12} lg={8}>
							<SelectElement
								name="year"
								label={t('ACTIONS.YEAR')}
								options={yearsOptions}
								rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							/>
						</Col>

						{periodType === 'Quarter' && (
							<Col span={24} md={12} lg={8}>
								<SelectElement
									name="period"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.QUARTER')}
									options={[
										{ value: 'QuarterOne', label: t('ACTIONS.QUARTER_ONE') },
										{ value: 'QuarterTwo', label: t('ACTIONS.QUARTER_TWO') },
										{ value: 'QuarterThree', label: t('ACTIONS.QUARTER_THREE') },
										{ value: 'QuarterFour', label: t('ACTIONS.QUARTER_FOUR') },
									]}
								/>
							</Col>
						)}

						<Col span={24} md={12} lg={8}>
							<SelectElement
								name="type"
								label={t('COMMON.TYPE')}
								options={typeOptions}
								rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							/>
						</Col>

						<Col span={24}>
							{isExist && !isChecking && (
								<Flex align="center" gap={5}>
									<InfoCircleOutlined style={{ color: 'red' }} />
									<Typography.Text type="danger" style={{ fontSize: 16 }}>
										{t('ACTIONS.FINANCIAL_STATEMENT_EXISTS')}
									</Typography.Text>
								</Flex>
							)}
						</Col>
					</Row>

					<Divider />

					<FormListFilesElement form={financialStatementForm} fieldName="files" title={t('ACTIONS.UPLOAD_DOCUMENTS')} />

					<Flex justify="flex-end" className="mt-5">
						<Button size="middle" type="primary" htmlType="submit">
							{t('ACTIONS.SEND_FOR_REVIEW')}
						</Button>
					</Flex>
				</Form>
			</Card>

			<VerificationModal
				open={verificationModalOpen}
				isLoading={isLoading}
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module="financialStatement"
			/>
		</>
	);
};

export default ActionFinancialStatementCreatePage;
